import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { optimizedImage } from "@/lib/imageUrl";

interface SiteAsset {
  key: string;
  url: string | null;
  label: string;
  category: string;
  hint_size: string | null;
  updated_at: string;
}

async function fetchSiteAssets(): Promise<SiteAsset[]> {
  const { data, error } = await supabase.from("site_assets").select("*");
  if (error) throw error;
  return (data as SiteAsset[]) ?? [];
}

export function useSiteAssets() {
  return useQuery({
    queryKey: ["site_assets"],
    queryFn: fetchSiteAssets,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Retorna a URL do asset. Informe `width` para servir a versão redimensionada
 * em WebP (evita baixar o original de vários MB em telas pequenas).
 */
export function useSiteAssetUrl(key: string, staticFallback: string, width?: number): string {
  const { data } = useSiteAssets();
  const asset = data?.find((a) => a.key === key);
  if (data && !asset?.url) {
    console.warn(
      `[ImageSync] "${key}" sem URL no banco: usando imagem estática local. Envie pelo Admin > Assets.`
    );
  }
  const url = asset?.url || staticFallback;
  return width ? optimizedImage(url, width) || url : url;
}

// Para componentes que precisam de múltiplos assets de uma vez
export function useSiteAssetMap(keys: string[]): Record<string, string | null> {
  const { data } = useSiteAssets();
  const map: Record<string, string | null> = {};
  for (const key of keys) {
    const asset = data?.find((a) => a.key === key);
    map[key] = asset?.url ?? null;
  }
  return map;
}

export function useInvalidateSiteAssets() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ["site_assets"] });
}
